import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Select,
  Modal,
  Button,
} from "@mui/material";
import { Colors } from "Utils/colors";
import { LocalImage } from "Utils/images";
import { OrderHistory } from "Components/Invoice/OrderHistory";
import { Customer } from "Features/services/customer.api";
import { useAppSelector } from "Hooks/reduxHook";
import { RootState } from "Features/store";
import {
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import moment from "moment";
import debounce from "lodash.debounce";
import { EditClientInformation } from "../EditClientInformation";
import CloseIcon from "@mui/icons-material/Close";
import { useCustomer } from "Context/CustomerContext";

const listData = [
  {
    id: "1",
    date: "11/12/23",
    order: {
      id: "QC12345",
      name: "Live End Anchor Encapsulated….",
      orderCount: 2,
      quantity: 2315,
    },
    total: "$11,432.03",
  },
  {
    id: "2",
    date: "3/15/23",
    order: {
      id: "QC12345",
      name: "Live End Anchor Encapsulated….",
      orderCount: 2,
      quantity: 2315,
    },
    total: "$11,432.03",
  },
  {
    id: "3",
    date: "5/2/22",
    order: {
      id: "QC12345",
      name: "Live End Anchor Encapsulated….",
      orderCount: 2,
      quantity: 2315,
    },
    total: "$11,432.03",
  },
];

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-40%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "12px",
  width: "75%",
  height: "90%",
};

export const ClientProfile = ({
  openEditModal,
}: {
  openEditModal: () => void;
}) => {
  const { customerData } = useCustomer();

  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [category, setCategory] = useState("");
  const [showDropDown, setShowDropDown] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const { access_token } = useAppSelector((state: RootState) => state.customer);
  const [customers, setCustomers] = useState<any[]>([]);

  const [customerId, setCustomerId] = useState<any>(null);

  const [isHovered, setIsHovered] = useState<boolean>(false);


  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleCategoryChange = (event: any) => {
    setCategory(event.target.value);
  };

  const handleCustomerSelect = (customer: any) => {
    setCustomerId(customer.qb_customer_id);
    setSelectedCustomer(customer);
    setShowDropDown(false);
    setSearchValue(customer.display_name);
  };

  const debouncedSearchCustomer = useCallback(
    debounce(async (value: string) => {
      const payload = {
        access_token: access_token,
        search_query: value,
        offset,
      };

      try {
        setLoading(true);
        const response = await Customer.instance.searchCustomer(payload);
        const data = response.data.data;
        setRows(data);
        setOffset(0);
        setShowDropDown(true);
      } catch (error) {
        console.error("Something went wrong", error);
      } finally {
        setLoading(false);
      }
    }, 400), // Debounce delay (in milliseconds)
    [offset, access_token]
  );

  const handleInputChange = (event: any) => {
    const value = event.target.value;
    setSearchValue(value);

    if (value) {
      debouncedSearchCustomer(value);
    } else {
      setSelectedCustomer(null);
    }
  };

  useEffect(() => {

    const fetchCustomers = async () => {
      const payload = {
        access_token: access_token,
        search_query: "",
        offset: 0,
      };

      try {
        setLoading(true);
        const response = await Customer.instance.searchCustomer(payload);
        const data = response.data.data;
        setCustomers(data);

        // Initialize state with customerData if available
        if (customerData) {
          const firstCustomer = customerData;
          setCustomerId(firstCustomer.qb_customer_id);
          setSearchValue(firstCustomer.display_name);
          setSelectedCustomer(firstCustomer);
        } else if (data.length > 0) {
          const firstCustomer = data[0];
          setCustomerId(firstCustomer.qb_customer_id);
          setSearchValue(firstCustomer.display_name);
          setSelectedCustomer(firstCustomer);
        }
      } catch (error) {
        console.error("Something went wrong", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [access_token, customerData]);

  const formattedDate = selectedCustomer
    ? moment(selectedCustomer.updated_at).isValid()
      ? moment(selectedCustomer.updated_at).format("MMMM Do, YYYY")
      : "- - -"
    : "-";

  const handleDropdownOpen = async () => {
    try {
      setLoading(true);
      const payload = {
        access_token: access_token,
        search_query: "",
        offset: 0,
      };
      const response = await Customer.instance.searchCustomer(payload);
      const data = response.data.data;
      setCustomers(data);
    } catch (error) {
      console.error("Something went wrong", error);
    } finally {
      setLoading(false);
    }
  };

  const renderCustomerItem = (customer: any) => (
    <MenuItem key={customer.id} onClick={() => handleCustomerSelect(customer)}>
      <ListItem>{customer.display_name}</ListItem>
    </MenuItem>
  );

  return (
    <Box
      sx={{
        flex: 1,
        border: `1px solid ${Colors.primary}`,
        borderRadius: "4px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          margin: "0px 8px",
          gap: "10px",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: "100%",
              marginBottom: "-16px",
              backgroundColor: "#fff",
            }}
          >
            <img
              src={LocalImage.companyLogo}
              style={{ width: "70px", height: "70px" }}
            />
          </Box>

          <Typography variant="body1" component="body" color={Colors.primary}>
            Client Profile
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            flexGrow: 1,
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ position: "relative",}}>
            <TextField
              placeholder="Search..."
              variant="outlined"
              value={searchValue}
              onChange={handleInputChange}
              sx={{
                width: "15vw",
                height: "40px",
                "& .MuiInputBase-root": {
                  height: "100%",
                },
                "& .MuiOutlinedInput-input": {
                  height: "100%",
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "4px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      value={category}
                      onChange={handleCategoryChange}
                      onOpen={handleDropdownOpen}
                      sx={{
                        width: "30px",
                        height: "100%",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          paddingRight: "0px",
                          fontSize: "14px",
                          height: "100%",
                        },
                      }}
                      IconComponent={ArrowDropDownIcon}
                    >
                      {loading ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : customers.length > 0 ? (
                        customers.map((customer) =>
                          renderCustomerItem(customer)
                        )
                      ) : (
                        <MenuItem disabled>No results</MenuItem>
                      )}
                    </Select>
                  </InputAdornment>
                ),
              }}
            />

            {showDropDown && searchValue && (
              <Paper
                sx={{
                  position: "absolute",
                  margin: "0 auto",
                  padding: "10px",
                  width: "15vw",
                  top: "40px",
                  zIndex: 1000,
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <List sx={{ maxHeight: "200px", overflow: "auto" }}>
                    {rows.map((customer) => (
                      <ListItem
                        key={customer.id}
                        button
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <ListItemText
                          primary={customer?.name}
                          secondary={customer?.display_name}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </Box>

          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px",
              marginBottom: "2px",
              mr: "20px",
              cursor: "pointer",
              transition: "border-color 0.3s ease",
              borderColor: isHovered ? "#FFAA2A" : "#ccc",
            }}
            className="clickable"
            onClick={handleOpen}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isHovered ? (
              <img
                src={LocalImage.hoverlineIcon}
                style={{ width: "18px", height: "18px" }}
                alt="Hover Icon"
              />
            ) : (
              <img
                src={LocalImage.defaultLineIcon}
                style={{ width: "18px", height: "18px" }}
                alt="Default Icon"
              />
            )}
          </Box>


        </Box>
      </Box>

      <Box
        sx={{
          border: `2px solid ${Colors.primary}`,
          margin: "0px 8px 0px",
        }}
      />
      <Box
        sx={{
          border: `6px solid #A3CEDC`,
          margin: "0px 8px 0px",
        }}
      />
      {/* customer data display section  */}

      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Box sx={{ flex: 1 }}>
          <CardContent>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              Name
            </Typography>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              {selectedCustomer?.display_name}
            </Typography>
          </CardContent>

          <CardContent sx={{ paddingTop: "0px", paddingBottom: "0px" }}>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              Billing Address
            </Typography>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              {selectedCustomer ? selectedCustomer?.billingAddress : ""}
            </Typography>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              {selectedCustomer
                ? `${selectedCustomer?.city} ${selectedCustomer?.country} ${selectedCustomer?.zip}`
                : "Bayshore ind 94326"}
            </Typography>
          </CardContent>

          <CardContent>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              Shipping Address
            </Typography>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              {selectedCustomer ? selectedCustomer?.shippingAddress : ""}
            </Typography>
            <Typography component={"body"} variant="body1" fontSize={"12px"}>
              {selectedCustomer
                ? `${selectedCustomer?.city} ${selectedCustomer?.country} ${selectedCustomer?.zip}`
                : "Bayshore ind 94326"}
            </Typography>
          </CardContent>
        </Box>

        <Box sx={{ flex: 0.9 }}>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="12px"
                  fontWeight="bold"
                >
                  Current Balance:
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="12px"
                  fontWeight="bold"
                  ml={1}
                  marginLeft={"10px"}
                >
                  {selectedCustomer
                    ? `$${selectedCustomer?.credit_line_balance || "0"}`
                    : "$0"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={2}>
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="12px"
                  mt={1}
                >
                  Available Credit:
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="12px"
                  mt={1}
                  marginLeft={"10px"}
                >
                  {selectedCustomer
                    ? `$${selectedCustomer?.total_credit_line || "0"}`
                    : "$0"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                >
                  Last Updated:
                </Typography>
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                  marginLeft={"10px"}
                >
                  {formattedDate}
                </Typography>
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                  marginLeft={"10px"}
                >
                  {selectedCustomer ? selectedCustomer?.lastUpdate : ""}
                </Typography>
              </Box>
            </CardContent>
          </Box>

          <Box>
            <CardContent sx={{ paddingTop: "0px", paddingBottom: "0px" }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                >
                  Email:
                </Typography>
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                  marginLeft={"10px"}
                >
                  {selectedCustomer
                    ? selectedCustomer?.email
                    : "Birds@Intuit.com"}
                </Typography>
              </Box>
            </CardContent>

            <CardContent sx={{ paddingTop: "0px", paddingBottom: "0px" }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                >
                  Phone:
                </Typography>
                <Typography
                  component={"body"}
                  variant="body1"
                  fontSize={"12px"}
                  marginLeft={"10px"}
                >
                  {selectedCustomer
                    ? selectedCustomer?.phoneNo
                    : "(650) 555-3311"}
                </Typography>
              </Box>
            </CardContent>
          </Box>
        </Box>
      </Box>

      {/* all order history detail modal */}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <>
          <Box sx={style}>
            <EditClientInformation
              selectedCustomer={selectedCustomer}
              customerId={customerId}
              closeModal={handleClose}
            />
          </Box>

          <Button
            sx={{
              backgroundColor: "#FFAA2A",
              padding: "8px 0px",
              float: "right",
              right: "2.5%",
              top: "2.5%",
              "&:hover": {
                backgroundColor: "#E69927",
              },
            }}
            onClick={handleClose}
          >
            <CloseIcon sx={{ fontSize: "32px", color: "#fff" }} />
          </Button>
        </>
      </Modal>

      {/* order history box section */}
      <Box sx={{ margin: "0px 16px" }}>
        <Typography variant="h6" component={"h6"} color={Colors.primary}>
          Order History
        </Typography>
        <Box sx={{ border: `2px solid ${Colors.primary}` }} />
        <Box sx={{ border: `4px solid #007CA40D` }} />

        <OrderHistory
          customerId={customerId}
          size="medium"
          rowBgSelection="even"
          containerStyle={{ overflowY: "auto", maxHeight: "40vh" }}
        />
      </Box>
    </Box>
  );
};
